import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import * as qs from 'qs';
import { endpoints } from '../constants/endpoints';
import { SpHttpCommonService } from './sp-http-common.service';
import { UserI } from '../interfaces/user.interface';
import { TotalumServerI } from '../interfaces/server.interface';
import { PlansI, TotalumInstanceI } from '../interfaces/instance.interface';
import { ReferralsPaginationI, ReferredInstancesResponseI, ReferralStatisticsResponseI } from '../interfaces/referrals.interface';
import { AffiliateDashboardI, AffiliateRewardsInfoI, ValidateReferralCodeResponseI } from '../interfaces/affiliate.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiSdkService {

  constructor(private spHttpCommonService:SpHttpCommonService) { }


  public async checkEmailExists(email: string) {
    const url = this.getUrl(endpoints.account.checkEmailExists);
    return await this.spHttpCommonService.httpPost(url, {email: email});
  }

  public async editUserData(user:UserI) {
    const url = this.getUrl(endpoints.account.editUserData);
    return await this.spHttpCommonService.httpPut(url, user);
  }

  public async updateLanguage(language: string) {
    const url = this.getUrl(endpoints.account.updateLanguage);
    return await this.spHttpCommonService.httpPut(url, { language });
  }

  public async login(email: string, secret: string, options?: {registerReason?:string, registeredDevice?: 'mobile' | 'desktop' | 'tablet', acquisitionAttr?: string, rdtConversionId?: string}) {
    const url = this.getUrl(endpoints.account.login);
    return await this.spHttpCommonService.httpPost(url, {email: email, secret: secret, options: options});
  }

  public async getFullUserData(noRedirect?: boolean) {
    const url = this.getUrl(endpoints.account.getFullUserData);
    return await this.spHttpCommonService.httpGet(url, null as any, noRedirect);
  }

  public async getUserStripeCard() {
    const url = this.getUrl(endpoints.stripe.getUserStripeCard);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async submitNewStripeCard(cardToken: string) {
    const url = this.getUrl(endpoints.stripe.submitNewStripeCard);
    return await this.spHttpCommonService.httpPost(url, {cardToken: cardToken});
  }

  public async getSubscriptionCheckoutUrl(instanceId: string, organizationId: string, plan: string, isAnnual?: boolean) {
    const url = this.getUrl(endpoints.stripe.subscriptionCheckout);
    return await this.spHttpCommonService.httpPost(url, {
      type: 'instance',
      instanceId: instanceId,
      organizationId: organizationId,
      plan: plan,
      isAnnual: false
    });
  }

  public async getCustomerPortalUrl() {
    const url = this.getUrl(endpoints.stripe.customerPortal);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getInstanceSubscription(instanceId: string) {
    const url = this.getUrl(endpoints.stripe.getInstanceSubscription, {instanceId: instanceId});
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async updateInstanceSubscription(instanceId: string, newPlan: string, customPrice?: number) {
    const url = this.getUrl(endpoints.stripe.updateInstanceSubscription);
    return await this.spHttpCommonService.httpPut(url, {
      instanceId: instanceId,
      newPlan: newPlan,
      customPrice: customPrice
    });
  }

  public async cancelInstanceSubscription(instanceId: string) {
    const url = this.getUrl(endpoints.stripe.cancelInstanceSubscription, {instanceId: instanceId});
    return await this.spHttpCommonService.httpDelete(url);
  }

  public async getInstanceByOrganizationId(organizationId: string) {
    const url = this.getUrl(endpoints.account.getInstanceByOrganizationId, {organizationId: organizationId});
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async checkInstanceNameIfExists(name: string, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.checkInstanceNameIfExists);
    return await this.spHttpCommonService.httpPost(url, {instanceName: name, recaptchaToken: recaptchaToken});
  }

  public async createInstance(instance: TotalumInstanceI, recaptchaToken:string, serverName?:string) {
    const url = this.getUrl(endpoints.account.createInstance);
    return await this.spHttpCommonService.httpPost(url, {instance, recaptchaToken, serverName});
  }

  /**
   * Paginated project list. An account can own hundreds of thousands of projects, so the
   * account page pages through this instead of reading the (capped) array on the user payload.
   * Returns { instances, total, skip, limit, planCounts }.
   */
  public async listInstances(options: {
    skip?: number;
    limit?: number;
    plan?: string;
    search?: string;
    serverId?: string;
    onlyWithoutServer?: boolean;
    sortField?: 'date' | 'name' | 'plan';
    sortDirection?: 'asc' | 'desc';
  } = {}) {
    const query = Object.entries(options)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    const url = this.getUrl(endpoints.account.listInstances) + (query ? `?${query}` : '');
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async editInstance(instance: TotalumInstanceI, recaptchaToken:string, serverName?:string) {
    const url = this.getUrl(endpoints.account.editInstance);
    return await this.spHttpCommonService.httpPut(url, {instance, recaptchaToken, serverName});
  }

  public async deleteInstanceById(instanceId: string) {
    const url = this.getUrl(endpoints.account.deleteInstanceById, {id: instanceId});
    return await this.spHttpCommonService.httpDelete(url,);
  }

  public async createServer(server: {name:string,description:string}, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.createServer);
    return await this.spHttpCommonService.httpPost(url, {server, recaptchaToken});
  }

  public async editServer(server: TotalumServerI, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.editServer);
    return await this.spHttpCommonService.httpPut(url, {server, recaptchaToken});
  }

  public async getServerByName(serverName: string) {
    const url = this.getUrl(endpoints.account.getServerByName, {serverName: serverName});
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async deleteServerById(serverId: string) {
    const url = this.getUrl(endpoints.account.deleteServerById, {id: serverId});
    return await this.spHttpCommonService.httpDelete(url,);
  }

  public async sendLoginUrl(email: string, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.sendLoginUrl);
    const result: any = await this.spHttpCommonService.httpPost(url, {email: email, recaptchaToken: recaptchaToken});
    // Francesc 2026-10-08: this area takes no new accounts — an address with no account registers on the platform.
    // Every sign-up / log-in screen calls this, so the hand-off is here once (the page never resolves past it).
    if (JSON.stringify(result?.error ?? '').includes('sign up at platform.totalum.app')) {
      window.location.assign(`https://platform.totalum.app/register?email=${encodeURIComponent(email)}`);
      return new Promise<never>(() => undefined);
    }
    return result;
  }

  public async crypt(data: string) {
    const url = this.getUrl(endpoints.account.crypt);
    return await this.spHttpCommonService.httpPost(url, {data: data});
  }

  public async moveInstanceFilesToAnother(organizationId:string, organizationIdToMoveFilesTo: string, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.moveInstanceFilesToAnother);
    return await this.spHttpCommonService.httpPost(url, {organizationId: organizationId, organizationIdToMoveFilesTo: organizationIdToMoveFilesTo,recaptchaToken: recaptchaToken});
  }

  public async toggleApiAccess(organizationId: string, apiAccessEnabled: boolean) {
    const url = this.getUrl(endpoints.account.toggleApiAccess);
    return await this.spHttpCommonService.httpPost(url, { organizationId, apiAccessEnabled });
  }

  public async changeInstancePlan(organizationId:string, newPlan: PlansI, recaptchaToken:string) {
    const url = this.getUrl(endpoints.account.changeInstancePlan);
    return await this.spHttpCommonService.httpPost(url, {organizationId: organizationId, newPlan: newPlan,recaptchaToken: recaptchaToken});
  }

  public async addCredits(organizationId:string, credits: number, usageType:string) {
    const url = this.getUrl(endpoints.account.addCredits);
    return await this.spHttpCommonService.httpPost(url, {organizationId: organizationId, credits: credits,usageType: usageType});
  }

  public async getInstanceCredits(organizationId:string) {
    const url = this.getUrl(endpoints.account.getInstanceCredits, {organizationId: organizationId});
    return await this.spHttpCommonService.httpPost(url, {});
  }

  public async getInstanceUsage(organizationId:string) {
    const url = this.getUrl(endpoints.account.getInstanceUsage, {organizationId: organizationId});
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  // Credits API methods
  public async getCreditsPacks() {
    const url = this.getUrl(endpoints.credits.getCreditsPacks);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getUserCreditsBalance() {
    const url = this.getUrl(endpoints.credits.getUserCredits);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async createCreditsCheckout(packId: string) {
    const url = this.getUrl(endpoints.credits.createCheckout);
    return await this.spHttpCommonService.httpPost(url, { packId: packId });
  }

  public async getAutoRechargeConfig() {
    const url = this.getUrl(endpoints.credits.autoRecharge);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async updateAutoRechargeConfig(payload: { enabled: boolean; thresholdCredits: number; rechargeCredits: number }) {
    const url = this.getUrl(endpoints.credits.autoRecharge);
    return await this.spHttpCommonService.httpPut(url, payload);
  }

  public async getSpendingAnalytics(from: string, to: string, organizationId?: string) {
    const params = new URLSearchParams({ from, to });
    if (organizationId) params.append('organizationId', organizationId);
    const url = this.getUrl(endpoints.credits.spendingAnalytics) + '?' + params.toString();
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  // Support Chat API methods (Admin only)
  /**
   * ⚠️ `tab` IS APPLIED SERVER-SIDE, BEFORE PAGING — see `getAllChatsWithUnreadFilter`.
   * It used to be a filter over the page the browser already held, which cannot work for
   * a tab that hides chats ('all' drops the credit-claim-only accounts) or one that
   * gathers them from anywhere in the collection ('favourites'). Omitted ⇒ 'all'.
   */
  public async getAllSupportChats(
    page: number = 1,
    limit: number = 20,
    search?: string,
    unreadOnly: boolean = false,
    tab?: string,
  ) {
    let url = this.getUrl(endpoints.assistantChat.getAll);
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) {
      params.append('search', search);
    }
    if (unreadOnly) {
      params.append('unreadOnly', 'true');
    }
    if (tab) {
      params.append('tab', tab);
    }
    url = url + '?' + params.toString();
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getSupportChatById(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.getById, { chatId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async sendSupportChatMessage(chatId: string, message: string, fileUrl?: string, fileName?: string, fileType?: string) {
    const url = this.getUrl(endpoints.assistantChat.sendMessage, { chatId });
    const body: any = { message, sender: 'assistant' };
    if (fileUrl) body.fileUrl = fileUrl;
    if (fileName) body.fileName = fileName;
    if (fileType) body.fileType = fileType;
    return await this.spHttpCommonService.httpPost(url, body);
  }

  public async markSupportChatAsSeen(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.markSeen, { chatId });
    return await this.spHttpCommonService.httpPost(url, {});
  }

  public async markSupportChatAsResponded(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.markResponded, { chatId });
    return await this.spHttpCommonService.httpPost(url, {});
  }

  /** Pin a support chat, or unpin it. The value is sent, never toggled server-side. */
  public async setSupportChatFavourite(chatId: string, favourite: boolean) {
    const url = this.getUrl(endpoints.assistantChat.favourite, { chatId });
    return await this.spHttpCommonService.httpPost(url, { favourite });
  }

  public async editSupportChatMessage(chatId: string, messageIndex: number, newMessage: string) {
    const url = this.getUrl(endpoints.assistantChat.editMessage, { chatId });
    return await this.spHttpCommonService.httpPut(url, { messageIndex, newMessage });
  }

  public async deleteSupportChatMessage(chatId: string, messageIndex: number) {
    const url = this.getUrl(endpoints.assistantChat.deleteMessage, { chatId }) + `?messageIndex=${messageIndex}`;
    return await this.spHttpCommonService.httpDelete(url);
  }

  public async getOrganizationsWithoutChats(search?: string) {
    let url = this.getUrl(endpoints.assistantChat.organizationsWithoutChats);
    if (search) {
      url = url + '?search=' + encodeURIComponent(search);
    }
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async createSupportChat(organizationId: string, initialMessage?: string) {
    const url = this.getUrl(endpoints.assistantChat.createChat);
    return await this.spHttpCommonService.httpPost(url, { organizationId, initialMessage });
  }

  /** The AI draft reply for a chat. A pure read — the backend never generates here. */
  public async getSupportChatSuggestion(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.getSuggestion, { chatId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  /**
   * ⭐⭐ The login URL for ONE customer project, for the CEO "Open" button.
   *
   * ⚠️ THE PASSWORD GOES IN THE BODY, NEVER THE QUERY STRING. A URL lands in browser
   * history, in referrer headers and in any proxy log on the way; a POST body does not.
   * The same applies to the URL that comes back, which is why this is not a GET.
   */
  public async getCeoProjectOpenUrl(organizationId: string, password: string) {
    const url = this.getUrl(endpoints.ceoDashboard.projectOpenUrl, { organizationId });
    return await this.spHttpCommonService.httpPost(url, { password });
  }

  /**
   * ⚠️ Deletes ONE customer project (sites, sandbox, instance row). `confirm` must be the
   * organizationId typed by hand — the server refuses anything else. The password rides
   * in the body for the same reason as `getCeoProjectOpenUrl`.
   */
  public async deleteCeoProject(organizationId: string, password: string, confirm: string) {
    const url = this.getUrl(endpoints.ceoDashboard.projectDelete, { organizationId });
    return await this.spHttpCommonService.httpPost(url, { password, confirm });
  }

  /** ⚠️ Deletes a user account AND all its projects. `confirm` must be the user's email typed by hand. */
  public async deleteCeoUser(userId: string, password: string, confirm: string) {
    const url = this.getUrl(endpoints.ceoDashboard.userDelete, { userId });
    return await this.spHttpCommonService.httpPost(url, { password, confirm });
  }

  // ── Bulk cleanup of free accounts (/ceo/free-cleanup) ───────────────────────
  public async getCeoBulkCleanupCandidates(params: { search?: string; skip?: number; limit?: number; all?: boolean }) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoDashboard.bulkCleanupCandidates), this.toHttpParams(params));
  }
  public async ceoBulkCleanupDryRun(userIds: string[]) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoDashboard.bulkCleanupDryRun), { userIds });
  }
  /** ⚠️ Deletes every project of the dry run's accounts and zeroes their credits. */
  public async ceoBulkCleanupExecute(body: { token: string; password: string; confirm: string; confirmRecent: string[] }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoDashboard.bulkCleanupExecute), body);
  }
  public async getCeoBulkCleanupJob(jobId: string) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoDashboard.bulkCleanupJob, { jobId }), null as any);
  }

  /**
   * ⭐⭐ A one-time link into ONE customer's totalum-platform account.
   *
   * ⚠️ POST, AND THE PASSWORD IS IN THE BODY, for the same reasons as the method
   * above — and one more: the RESPONSE is a bearer credential too. A GET would put
   * both the password and the returned link into browser history and proxy logs.
   *
   * Unlike `getCeoProjectOpenUrl`, the link this returns is meant to be shown and
   * copied. It is single-use and expires in five minutes; see
   * `platform-impersonation.service.ts` for why that makes it safe to render.
   */
  public async getCeoPlatformLoginUrl(userId: string, password: string) {
    const url = this.getUrl(endpoints.ceoDashboard.platformLoginUrl, { userId });
    return await this.spHttpCommonService.httpPost(url, { password });
  }

  /**
   * ⭐ Upload a file to attach to a support reply.
   *
   * ⚠️ NO `Content-Type` IS SET, DELIBERATELY. `HttpClient` writes the multipart
   * boundary itself for a `FormData` body; setting the header by hand omits the
   * boundary and the server parses nothing. `getAppHeaders` already leaves it off.
   *
   * ⚠️ THE SERVER DECIDES THE TYPE FROM THE BYTES. Whatever the browser reports is
   * only a hint — see `support-attachment.service.ts`.
   */
  public async uploadSupportChatFile(file: File) {
    const url = this.getUrl(endpoints.assistantChat.uploadFile);
    const form = new FormData();
    form.append('file', file, file.name);
    return await this.spHttpCommonService.httpPost(url, form);
  }

  /**
   * ⭐ Translate ONE customer message into English.
   *
   * ⚠️ IDENTIFIED BY `messageAt` (the message's `createdAt`), NOT by its index.
   * Deleting a message renumbers the array, so an index held by an open panel can
   * point at a different message by the time the button is pressed.
   *
   * A translation the backend has already cached comes back with `cached: true` and
   * costs nothing.
   */
  public async translateSupportChatMessage(chatId: string, messageAt: string) {
    const url = this.getUrl(endpoints.assistantChat.translateMessage, { chatId });
    return await this.spHttpCommonService.httpPost(url, { messageAt });
  }

  /**
   * ⭐ Proofread the reply currently in the composer.
   *
   * ⚠️ NO `chatId`, AND NOTHING IS STORED. The corrected text only exists in this
   * response — the panel shows it in a modal and the agent chooses whether to apply
   * it. The draft in the textarea is never touched by the server.
   */
  /**
   * ⭐ THE CHAT ID TRAVELS WITH THE DRAFT, and the context does not.
   *
   * The server reads the customer's own last message and their first name from
   * that chat, so the polish can judge whether the wording reads as an answer to
   * the question. Sending the context from here instead would let the browser put
   * arbitrary text into a prompt that rewrites what an agent is about to send.
   */
  public async polishSupportChatDraft(text: string, chatId?: string) {
    const url = this.getUrl(endpoints.assistantChat.polish);
    return await this.spHttpCommonService.httpPost(url, chatId ? { text, chatId } : { text });
  }

  /** The agent rejected this draft: never show it again, on any device. */
  public async discardSupportChatSuggestion(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.discardSuggestion, { chatId });
    return await this.spHttpCommonService.httpPost(url, {});
  }

  public async getChatUserCredits(chatId: string) {
    const url = this.getUrl(endpoints.assistantChat.getUserCredits, { chatId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async giveCreditsToChatUser(chatId: string, credits: number) {
    const url = this.getUrl(endpoints.assistantChat.giveCredits, { chatId });
    return await this.spHttpCommonService.httpPost(url, { credits });
  }

  public async removeCreditsFromChatUser(chatId: string, credits: number) {
    const url = this.getUrl(endpoints.assistantChat.removeCredits, { chatId });
    return await this.spHttpCommonService.httpPost(url, { credits });
  }

  // Referrals API methods
  public async getReferredInstances(pagination?: ReferralsPaginationI): Promise<{ success?: { data: ReferredInstancesResponseI }, error?: any }> {
    let url = this.getUrl(endpoints.referrals.getInstances);
    const params = new URLSearchParams();
    if (pagination?.skip !== undefined) {
      params.append('skip', pagination.skip.toString());
    }
    if (pagination?.limit !== undefined) {
      params.append('limit', pagination.limit.toString());
    }
    if (params.toString()) {
      url = url + '?' + params.toString();
    }
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getReferralStatistics(): Promise<{ success?: { data: ReferralStatisticsResponseI }, error?: any }> {
    const url = this.getUrl(endpoints.referrals.getStatistics);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  // Affiliate API methods
  public async getAffiliateDashboard(): Promise<{ success?: { data: AffiliateDashboardI }, error?: any }> {
    const url = this.getUrl(endpoints.affiliate.getDashboard);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async validateReferralCode(code: string): Promise<{ success?: { data: ValidateReferralCodeResponseI }, error?: any }> {
    const url = this.getUrl(endpoints.affiliate.validateCode, { code });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getAffiliateRewardsInfo(): Promise<{ success?: { data: AffiliateRewardsInfoI }, error?: any }> {
    const url = this.getUrl(endpoints.affiliate.getRewardsInfo);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async updateAffiliateNotifications(notifications: { onReferralSignup: boolean, onReferralConversion: boolean }): Promise<{ success?: { data: any }, error?: any }> {
    const url = this.getUrl(endpoints.affiliate.updateNotifications);
    return await this.spHttpCommonService.httpPut(url, notifications);
  }

  // API Keys methods
  public async getApiKeys() {
    const url = this.getUrl(endpoints.apiKeys.list);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async createApiKey(name: string, allowedProjects?: string[]) {
    const url = this.getUrl(endpoints.apiKeys.create);
    const body: any = { name };
    if (allowedProjects && allowedProjects.length > 0) body.allowedProjects = allowedProjects;
    return await this.spHttpCommonService.httpPost(url, body);
  }

  public async getDecryptedApiKey(id: string) {
    const url = this.getUrl(endpoints.apiKeys.decrypt, { id });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async deleteApiKey(id: string) {
    const url = this.getUrl(endpoints.apiKeys.delete, { id });
    return await this.spHttpCommonService.httpDelete(url);
  }

  public async updateApiKeyAllowedProjects(id: string, allowedProjects: string[] | null) {
    const url = this.getUrl(endpoints.apiKeys.updateAllowedProjects, { id });
    return await this.spHttpCommonService.httpPatch(url, { allowedProjects });
  }

  // VCaaS notification preferences
  public async updateVcaasNotifications(notifications: { emailOnFirstPromptCompleted: boolean, emailOnLimitReached: boolean }) {
    const url = this.getUrl(endpoints.account.editUserData);
    return await this.spHttpCommonService.httpPut(url, { vcaasNotifications: notifications });
  }

  // VCaaS methods
  public async getVcaasCreditCosts() {
    const url = this.getUrl(endpoints.vcaas.getCreditCosts);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getWebhooks() {
    const url = this.getUrl(endpoints.vcaas.getWebhooks);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async createWebhook(urlValue: string, event: string, headers?: Record<string, string>) {
    const url = this.getUrl(endpoints.vcaas.createWebhook);
    return await this.spHttpCommonService.httpPut(url, { url: urlValue, event, headers });
  }

  public async deleteWebhook(webhookId: string) {
    const url = this.getUrl(endpoints.vcaas.deleteWebhook, { id: webhookId });
    return await this.spHttpCommonService.httpDelete(url);
  }

  // ── CEO dashboard (speedparadigm account only; the backend enforces this) ──────────

  public async getCeoOverview(filters?: Record<string, any>) {
    const url = this.getUrl(endpoints.ceoDashboard.overview);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  /** ⭐ The growth console payload. `filters` values are comma-joined lists per dimension. */
  public async getCeoGrowth(params?: Record<string, any>) {
    const url = this.getUrl(endpoints.ceoDashboard.growth);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(params));
  }

  /** The same processed payload as a downloadable JSON document (never raw rows). */
  public async getCeoGrowthExport(params?: Record<string, any>): Promise<Blob | null> {
    const query = this.toHttpParams(params).toString();
    const url = this.getUrl(endpoints.ceoDashboard.growthExport) + (query ? `?${query}` : '');
    return await this.spHttpCommonService.httpGetBlob(url);
  }

  // ── Credit packs CRUD (/ceo/credits-packs) ───────────────────────────────────
  public async getCeoCreditsPacks() {
    const url = this.getUrl(endpoints.ceoDashboard.creditsPacks);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async createCeoCreditsPack(body: { quantity: number; price: number; active?: boolean }) {
    const url = this.getUrl(endpoints.ceoDashboard.creditsPacks);
    return await this.spHttpCommonService.httpPost(url, body);
  }

  public async updateCeoCreditsPack(packId: string, body: { quantity?: number; price?: number; active?: boolean }) {
    const url = this.getUrl(endpoints.ceoDashboard.creditsPack, { packId });
    return await this.spHttpCommonService.httpPatch(url, body);
  }

  public async deleteCeoCreditsPack(packId: string) {
    const url = this.getUrl(endpoints.ceoDashboard.creditsPack, { packId });
    return await this.spHttpCommonService.httpDelete(url);
  }

  // ── The signup email-domain blocklist (/ceo/blocked-domains) ────────────────
  public async getCeoBlockedEmailDomains() {
    const url = this.getUrl(endpoints.ceoDashboard.blockedEmailDomains);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async addCeoBlockedEmailDomain(domain: string, reason?: string) {
    const url = this.getUrl(endpoints.ceoDashboard.blockedEmailDomains);
    return await this.spHttpCommonService.httpPost(url, { domain, ...(reason ? { reason } : {}) });
  }

  // ── The signup IP blocklist (/ceo/block) ────────────────────────────────────
  /** Refused sign-ups counted by rule (F1): `{ from, to, groupBy, total, totals, byPath, series, latest }`. */
  public async getCeoSignupRefusals(query: { from: string; to: string; groupBy: 'day' | 'week'; limit: number }) {
    const url = this.getUrl(endpoints.ceoDashboard.signupRefusals);
    return await this.spHttpCommonService.httpGet(url, new HttpParams({ fromObject: { ...query, limit: String(query.limit) } }));
  }
  public async getCeoBlockedIps() {
    const url = this.getUrl(endpoints.ceoDashboard.blockedIps);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }
  public async addCeoBlockedIp(ip: string, reason?: string) {
    const url = this.getUrl(endpoints.ceoDashboard.blockedIps);
    return await this.spHttpCommonService.httpPost(url, { ip, ...(reason ? { reason } : {}) });
  }
  public async removeCeoBlockedIp(ip: string) {
    // ⚠️ ENCODED: a CIDR entry contains a `/`, which would otherwise read as another path segment.
    const url = this.getUrl(endpoints.ceoDashboard.blockedIp, { ip: encodeURIComponent(ip) });
    return await this.spHttpCommonService.httpDelete(url);
  }

  public async removeCeoBlockedEmailDomain(domain: string) {
    const url = this.getUrl(endpoints.ceoDashboard.blockedEmailDomain, { domain: encodeURIComponent(domain) });
    return await this.spHttpCommonService.httpDelete(url);
  }

  public async getCeoFilterOptions() {
    const url = this.getUrl(endpoints.ceoDashboard.filterOptions);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getCeoRecentPurchases(limit = 50) {
    const url = this.getUrl(endpoints.ceoDashboard.recentPurchases);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams({ limit }));
  }

  public async getCeoUsers(filters?: Record<string, any>) {
    const url = this.getUrl(endpoints.ceoDashboard.users);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  public async getCeoUserDetail(userId: string) {
    const url = this.getUrl(endpoints.ceoDashboard.userDetail, { userId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getCeoProjects(filters?: Record<string, any>) {
    const url = this.getUrl(endpoints.ceoDashboard.projects);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  public async getCeoAcquisitionReport(filters?: Record<string, any>) {
    const url = this.getUrl(endpoints.ceoDashboard.acquisitionReport);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  public async giveCreditsToCeoUser(userId: string, credits: number) {
    const url = this.getUrl(endpoints.ceoDashboard.giveCredits, { userId });
    return await this.spHttpCommonService.httpPost(url, { credits });
  }

  public async removeCreditsFromCeoUser(userId: string, credits: number) {
    const url = this.getUrl(endpoints.ceoDashboard.removeCredits, { userId });
    return await this.spHttpCommonService.httpPost(url, { credits });
  }

  /**
   * ⭐ Puts a platform account on `plan` with no invoice. The refusals are typed
   * (`HAS_STRIPE_SUBSCRIPTION`, `DAILY_LIMIT_REACHED`, ...) because the caller has
   * to tell them apart — see the plan panel in ceo-user-detail.
   */
  public async setCeoUserPlan(userId: string, plan: string, reason?: string) {
    const url = this.getUrl(endpoints.ceoDashboard.setUserPlan, { userId });
    return await this.spHttpCommonService.httpPost(url, { plan, reason });
  }

  /**
   * ⭐ Overrides how many projects one account may own and how fast it may create
   * them. Absent keys are left alone; `null` clears an override and hands that
   * number back to the plan's default.
   *
   * ⚠️ SEND `null`, NEVER `0`, FOR "no override". `0` is a real, enforced value —
   * it is how an account is frozen without changing its plan.
   */
  public async setCeoUserProjectLimits(
    userId: string,
    limits: { maxProjects?: number | null; projectsPerMinute?: number | null; projectsPerHour?: number | null },
    reason?: string,
  ) {
    const url = this.getUrl(endpoints.ceoDashboard.setUserProjectLimits, { userId });
    return await this.spHttpCommonService.httpPost(url, { ...limits, reason });
  }

  public async getCeoPlanChangeUsage() {
    const url = this.getUrl(endpoints.ceoDashboard.planChangeUsage);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  // ── Shareable template links (/ceo/templates) ─────────────────────────────────
  //
  // ⚠️ THE LIST CARRIES ONE BEARER CREDENTIAL PER ROW. An export code lets its holder
  // import that project into any empty project of their own — which is precisely why
  // the public launch link carries a short ALIAS and the code stays server-side
  // everywhere except this admin screen. Do not log these responses, and do not put a
  // code in a URL.

  public async getCeoImportTemplates() {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplates);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  /**
   * ⚠️ TAKES `FormData`, BECAUSE A TEMPLATE CARRIES A PICTURE. `getAppHeaders` does
   * not set a `Content-Type` (that line is commented out), so Angular derives
   * `multipart/form-data` and its boundary from the body itself — setting one by hand
   * here would corrupt the boundary and the file would never arrive.
   */
  public async createCeoImportTemplate(body: FormData) {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplates);
    return await this.spHttpCommonService.httpPost(url, body);
  }

  /**
   * ⚠️ AN OMITTED / EMPTY `exportCode` MEANS "LEAVE IT ALONE". The form shows the code
   * masked, so an edit that only renames a template submits an empty field — and the
   * backend deliberately reads that as "unchanged" rather than "clear it", which would
   * silently break every link to that template.
   */
  public async updateCeoImportTemplate(templateId: string, body: FormData) {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplate, { templateId });
    return await this.spHttpCommonService.httpPatch(url, body);
  }

  public async deleteCeoImportTemplate(templateId: string) {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplate, { templateId });
    return await this.spHttpCommonService.httpDelete(url);
  }

  /**
   * ⚠️⚠️ RESOLVES TO A LIVE SERVER SECRET. Never log it, never store it, never put it
   * in a URL — the page holds it in memory only while the panel is open. POST because
   * a GET would leak it into the history and into proxy logs.
   */
  public async revealCeoImportTemplateApiSecret() {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplateApiCredentials);
    return await this.spHttpCommonService.httpPost(url, {});
  }

  public async getCeoImportTemplateClaims(templateId: string) {
    const url = this.getUrl(endpoints.ceoDashboard.importTemplateClaims, { templateId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  // ── OAuth worker fleet (/ceo/oauths; same CEO-only gate as the dashboard) ─────────
  //
  // Every one of these is proxied by account-backend to the firefox-automation root
  // server. The browser must never call that host directly — it is guarded by a single
  // shared secret that cannot be shipped to a client.

  public async getOauthWorkers() {
    const url = this.getUrl(endpoints.oauthServers.workers);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getOauthSummary(filters?: { from?: string; to?: string }) {
    const url = this.getUrl(endpoints.oauthServers.summary);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  public async getOauthActivity(filters?: Record<string, any>) {
    const url = this.getUrl(endpoints.oauthServers.activity);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams(filters));
  }

  /**
   * The API credit balance over time, already reduced to ~120 points server-side.
   * `days` is capped at 30 by the backend and bounded in practice by the bot's
   * seven-day activity log — read `coveredHours` off the response for the label.
   */
  public async getOauthCreditHistory(days = 7) {
    const url = this.getUrl(endpoints.oauthServers.creditHistory);
    return await this.spHttpCommonService.httpGet(url, this.toHttpParams({ days }));
  }

  public async getOauthQueueStatus() {
    const url = this.getUrl(endpoints.oauthServers.queueStatus);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getOauthLoginStatus() {
    const url = this.getUrl(endpoints.oauthServers.loginStatus);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getOauthSelfHealStatus() {
    const url = this.getUrl(endpoints.oauthServers.selfHealStatus);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  public async getOauthProvisions() {
    const url = this.getUrl(endpoints.oauthServers.provisions);
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  /** Absolute URL of a failure screenshot. Needs the JWT, so fetch it as a blob. */
  public getOauthScreenshotUrl(name: string) {
    return this.getUrl(endpoints.oauthServers.screenshot, { name });
  }

  /**
   * ⭐ The screenshot BYTES, as a blob.
   *
   * ⚠️ NOT A URL FOR AN `<img>` TAG. The proxy is behind the CEO JWT and an `<img>`
   * cannot carry a header, which is why the helper above was never actually usable on
   * its own — it existed with a comment saying "fetch it as a blob" and nothing did.
   */
  public async getOauthScreenshotBlob(name: string): Promise<Blob | null> {
    return await this.spHttpCommonService.httpGetBlob(this.getOauthScreenshotUrl(name));
  }

  public async checkOauthEmail(email: string) {
    const url = this.getUrl(endpoints.oauthServers.checkEmail);
    return await this.spHttpCommonService.httpPost(url, { email });
  }

  public async createOauthServer(email: string) {
    const url = this.getUrl(endpoints.oauthServers.createWorker);
    return await this.spHttpCommonService.httpPost(url, { email });
  }

  public async resumeOauthProvision(jobId: string) {
    const url = this.getUrl(endpoints.oauthServers.resumeProvision, { jobId });
    return await this.spHttpCommonService.httpPost(url, {});
  }

  public async logoutOauthAccount(email: string) {
    const url = this.getUrl(endpoints.oauthServers.logout);
    return await this.spHttpCommonService.httpPost(url, { email });
  }

  public async startOauthLogin(workerId: string) {
    const url = this.getUrl(endpoints.oauthServers.startLogin);
    return await this.spHttpCommonService.httpPost(url, { workerId });
  }

  public async submitOauthLoginCode(workerId: string, code: string) {
    const url = this.getUrl(endpoints.oauthServers.submitLoginCode);
    return await this.spHttpCommonService.httpPost(url, { workerId, code });
  }

  public async setOauthWorkerState(workerId: string, active: boolean) {
    const url = this.getUrl(endpoints.oauthServers.workerState, { workerId });
    return await this.spHttpCommonService.httpPost(url, { active });
  }

  /** The exact VM a delete would destroy. Read-only; runs every safety check. */
  public async previewOauthWorkerDeletion(workerId: string) {
    const url = this.getUrl(endpoints.oauthServers.deleteWorkerPreview, { workerId });
    return await this.spHttpCommonService.httpGet(url, null as any);
  }

  /** Destroys the worker's Clouding VM. Irreversible — the backend re-verifies identity. */
  public async deleteOauthWorker(workerId: string, confirmEmail: string) {
    const url = this.getUrl(endpoints.oauthServers.deleteWorker, { workerId });
    return await this.spHttpCommonService.httpPost(url, { confirmEmail });
  }

  public async deployOauthFleet() {
    const url = this.getUrl(endpoints.oauthServers.deployAll);
    return await this.spHttpCommonService.httpPost(url, {});
  }

  /** Drops empty/undefined filters so they aren't sent as blank query params. */
  private toHttpParams(filters?: Record<string, any>): HttpParams {
    let params = new HttpParams();
    if (!filters) return params;

    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value === undefined || value === null || value === '') return;
      params = params.set(key, String(value));
    });
    return params;
  }

  // ── CEO email campaigns + quick contact (/ceo/emails) ─────────────────────────
  // ⚠️ PATCH/DELETE failures arrive as `error.error.errors` (the http service stores the
  // whole HttpErrorResponse for those verbs); GET/POST as `error.errors`. Callers read
  // both through `ceoEmailError()` in `ceo-emails.interface.ts`.

  public async getCeoEmailsMeta() {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.meta), null as any);
  }

  public async previewCeoEmailAudience(body: { audience: any; page?: number; limit?: number; search?: string; applyGlobalDiscard?: boolean }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.audiencePreview), body);
  }

  public async suggestCeoEmailAudience(body: { prompt: string; audience?: any }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.audienceAi), body);
  }

  public async searchCeoEmailUsers(q: string) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.searchUsers), this.toHttpParams({ q }));
  }

  /** Is this a normal volume for that From address? Shown in the review dialog. */
  public async checkCeoEmailWarmup(fromEmail: string, count: number) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.warmup), this.toHttpParams({ fromEmail, count }));
  }

  public async previewCeoEmail(body: { kind: 'campaign' | 'direct'; message: any; userId?: string | null }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.preview), body);
  }

  public async sendCeoTestEmail(body: { kind: 'campaign' | 'direct'; message: any; userId?: string | null; to: string }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.test), body);
  }

  public async listCeoQuickTemplates() {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.quickTemplates), null as any);
  }

  public async createCeoQuickTemplate(body: any) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.quickTemplates), body);
  }

  public async updateCeoQuickTemplate(templateId: string, body: any) {
    return await this.spHttpCommonService.httpPatch(this.getUrl(endpoints.ceoEmails.quickTemplate, { templateId }), body);
  }

  public async deleteCeoQuickTemplate(templateId: string) {
    return await this.spHttpCommonService.httpDelete(this.getUrl(endpoints.ceoEmails.quickTemplate, { templateId }));
  }

  public async contactCeoUser(userId: string, body: any) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.contactUser, { userId }), body);
  }

  public async getCeoUserContactHistory(userId: string) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.contactHistory, { userId }), null as any);
  }

  public async listCeoCampaigns() {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.campaigns), null as any);
  }

  /** `refreshStats` asks the backend to re-read SendGrid stats when they are over 5 minutes old. */
  public async getCeoCampaign(campaignId: string, refreshStats = false) {
    return await this.spHttpCommonService.httpGet(
      this.getUrl(endpoints.ceoEmails.campaign, { campaignId }),
      this.toHttpParams(refreshStats ? { stats: 1 } : {}),
    );
  }

  public async createCeoCampaign(body: any) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.campaigns), body);
  }

  public async updateCeoCampaign(campaignId: string, body: any) {
    return await this.spHttpCommonService.httpPatch(this.getUrl(endpoints.ceoEmails.campaign, { campaignId }), body);
  }

  public async deleteCeoCampaign(campaignId: string) {
    return await this.spHttpCommonService.httpDelete(this.getUrl(endpoints.ceoEmails.campaign, { campaignId }));
  }

  public async ceoCampaignAction(campaignId: string, action: 'send' | 'pause' | 'resume' | 'cancel' | 'duplicate', body: any = {}) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.campaignAction, { campaignId, action }), body);
  }

  /** The discard filter applied to every campaign (current and future). */
  public async getCeoGlobalDiscard() {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.globalDiscard), null as any);
  }

  public async setCeoGlobalDiscard(body: { combinator: 'and' | 'or'; groups: any[] }) {
    return await this.spHttpCommonService.httpPut(this.getUrl(endpoints.ceoEmails.globalDiscard), body);
  }

  public async listCeoExcludedUsers(query: Record<string, any>) {
    return await this.spHttpCommonService.httpGet(this.getUrl(endpoints.ceoEmails.exclusions), this.toHttpParams(query));
  }

  /** `{ userIds }` for picked people, or `{ audience }` to exclude everybody a filter matches. */
  public async excludeCeoEmailUsers(body: { userIds?: string[]; audience?: any }) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.exclusions), body);
  }

  public async unexcludeCeoEmailUsers(userIds: string[]) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.exclusionsRemove), { userIds });
  }

  /**
   * An image for a template campaign. Re-encoded server-side (≤1200px, JPEG/PNG) and
   * stored with a long-lived URL. `FormData` — the browser sets the multipart boundary.
   */
  public async uploadCeoEmailImage(file: File) {
    const form = new FormData();
    form.append('image', file, file.name);
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.images), form);
  }

  /** The support chat's Polish, same service; `userId` lets the server read the recipient's name. */
  public async polishCeoEmailText(text: string, userId?: string | null) {
    return await this.spHttpCommonService.httpPost(this.getUrl(endpoints.ceoEmails.polish), userId ? { text, userId } : { text });
  }

  private getUrl(url:string, params?:any){
    if(params){
      Object.keys(params).forEach(key => {
        url = url.replace(`:${key}`, params[key]);
      });
    }
    return environment.apiDomain + url;
  }


}
